import { useCallback, useEffect } from 'react'
import { useChainId } from 'wagmi'

import { useNetworkChange } from '../../dialogs/select-network/logic/useNetworkChange'
import { useSettingsDialog } from '../settings-dialog/logic/useSettingsDialog'
import { ActionsViewState } from '../types'
import { InjectedActionsContext, Objective } from './types'
import { useActionHandlers } from './useActionHandlers'
import { useActionsContext } from './useActionsContext'
import { useBatchActionHandler } from './useBatchActionHandler'
import { useCanWalletBatch } from './useCanWalletBatch'
import { useCreateActions } from './useCreateActions'
import { useSnapshot } from '@/domain/hooks/useSnapshot'
import { useSandbox } from '@/domain/sandbox'
import { useActionsSettings } from '@/domain/state'
import { useActionsSharedState } from '@/domain/state/actions-shared-state'
import { useTermsOfService } from '@/domain/state/compliance'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { randomHexId } from '@/utils/random'

export interface UseActionsParams {
  objectives: Objective[]
  context: InjectedActionsContext
  onFinish: () => void
  enabled: boolean
}

export interface UseActionsResult {
  reset: () => void
  viewState: ActionsViewState
}

export function useActions({
  objectives: _objectives,
  context: _injectedContext,
  onFinish,
  enabled,
}: UseActionsParams): UseActionsResult {
  const chainId = useChainId()
  const { account, walletMode } = useConnectedAddress({ throwOnNotConnected: false })
  const { isInSandbox } = useSandbox()
  const { agreedToTermsOfService, saveAgreedToTermsOfService } = useTermsOfService()
  const networkChange = useNetworkChange()

  const [objectives, snapshotObjectives] = useSnapshot(_objectives)
  const [injectedContext, snapshotInjectedContext] = useSnapshot(_injectedContext)
  const [actionsInvocationId, snapshotActionsInvocationId] = useSnapshot(randomHexId())

  const expectedChainId = injectedContext.chainId
  const matchesExpectedChainId = expectedChainId === chainId
  const actionsEnabled = enabled && matchesExpectedChainId && walletMode !== 'watch-wallet'

  const {
    data: { canWalletBatch },
  } = useCanWalletBatch()
  const settingsDialogProps = useSettingsDialog()
  const accountAddress = settingsDialogProps.batchTransactionsSettings.accountAddress
  const { setActionsSettingsEnabled } = useActionsSharedState()
  const { getTxBatchingPreference, setTxBatchingPreference } = useActionsSettings()
  const batchingEnabled = getTxBatchingPreference(accountAddress) && canWalletBatch

  const { context, resetContext } = useActionsContext(injectedContext)
  const actions = useCreateActions({ objectives, context, batchingEnabled })

  const batchActionHandler = useBatchActionHandler({
    actions,
    enabled: actionsEnabled && batchingEnabled,
    onFinish,
    context,
  })
  const {
    actionHandlers,
    actionsInProgress,
    reset: resetActionHandlers,
  } = useActionHandlers({
    actions,
    context,
    enabled: actionsEnabled && !batchingEnabled,
    onFinish,
    actionsInvocationId,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(() => {
    if (
      batchActionHandler?.state.status === 'error' &&
      batchActionHandler.state.errorKind === 'wallet-upgrade-rejection'
    ) {
      setTxBatchingPreference(accountAddress, false)
      batchActionHandler.reset()
    }
  }, [batchActionHandler?.state.status, accountAddress, setTxBatchingPreference])

  useEffect(() => {
    setActionsSettingsEnabled(!actionsInProgress)
  }, [actionsInProgress, setActionsSettingsEnabled])

  const reset = useCallback(() => {
    snapshotActionsInvocationId()
    snapshotObjectives()
    snapshotInjectedContext()
    resetActionHandlers()
    batchActionHandler.reset()
    resetContext()
  }, [
    snapshotObjectives,
    snapshotInjectedContext,
    snapshotActionsInvocationId,
    resetActionHandlers,
    batchActionHandler.reset,
    resetContext,
  ])

  const viewState: ActionsViewState = (() => {
    if (walletMode === 'watch-wallet') {
      return { type: 'watch-wallet' }
    }

    if (account === undefined) {
      return { type: 'not-connected' }
    }

    if (
      import.meta.env.VITE_FEATURE_TOS_BEFORE_ACTIONS === '1' &&
      account &&
      !isInSandbox &&
      !agreedToTermsOfService(account)
    ) {
      return { type: 'tos-not-accepted', onAccept: () => saveAgreedToTermsOfService(account) }
    }

    if (!matchesExpectedChainId) {
      return { type: 'chain-mismatch', expectedChainId, currentChainId: chainId, networkChange }
    }

    if (actions.length === 0) {
      return { type: 'no-actions' }
    }

    return {
      type: 'active',
      actionHandlers,
      batchActionHandler: batchingEnabled ? batchActionHandler : undefined,
      settingsDialogProps,
      canWalletBatch,
    }
  })()

  return { viewState, reset }
}
